/**
 * The AjaxHandler class provides a simple and reusable way to make AJAX requests in WordPress.
 * 
 * This class is designed to handle common AJAX operations, including sending requests with 
 * security verification using nonces and handling both success and error responses.
 * 
 * Dependencies:
 * - jQuery: This class uses jQuery's `$.ajax` method to send requests.
 * 
 * Example Usage:
 * - Initialize an instance of AjaxHandler with the AJAX URL and nonce:
 *   ```javascript
 *   const ajaxHandler = new AjaxHandler(ajaxData.ajaxurl, ajaxData.nonce);
 *   ```
 * - Make a request:
 *   ```javascript
 *   ajaxHandler.makeRequest(
 *       'my_action',
 *       { key: 'value' },
 *       (response) => { console.log('Success:', response); },
 *       (error) => { console.error('Error:', error); }
 *   );
 *   ```
 * 
 * @class AjaxHandler
 */

class AjaxHandler {
    /**
     * Constructor to initialize the AJAX handler with the AJAX URL and security nonce.
     * 
     * @param {string} ajaxUrl - The URL for the AJAX requests (usually `admin-ajax.php`).
     * @param {string} nonce - The nonce for security verification, ensuring the request is valid.
     */
    constructor(ajaxUrl, nonce) {
        this.ajaxUrl = ajaxUrl;
        this.nonce = nonce;
    }

    /**
     * General method to make an AJAX request.
     * 
     * This method sends a POST request to the specified AJAX URL with the provided data.
     * The `action` parameter is used to trigger the appropriate WordPress AJAX handler.
     * 
     * @param {string} action - The WordPress AJAX action to trigger.
     * @param {object} data - The data to send with the request. Additional data can be included.
     * @param {function} successCallback - The function to call when the request is successful.
     * @param {function} errorCallback - The function to call if the request fails.
     * 
     * @return {void}
     */
    makeRequest(action, data, successCallback, errorCallback) {     
        $.ajax({
            url: this.ajaxUrl,
            type: 'POST',
            data: { ...data, action, nonce: this.nonce },
            success: successCallback,
            error: errorCallback
        });
    }
}

export default AjaxHandler;